import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import useEmblaCarousel from "embla-carousel-react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

const enum fields {
    heading = "heading",
    autoScroll = "autoScroll",
    categories = "categories",
}

const AUTO_SCROLL_INTERVAL_MS = 3000;

export interface CategoryModel {
    fields: {
        image: string;
        name: string;
        destination: LinkFieldValue;
        openInNewWindow: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.autoScroll]: boolean;
        [fields.categories]: CategoryModel[];
    };
}

export interface PopularCategoriesCarouselStyles {
    wrapper?: InjectableCss;
    heading?: TypographyProps;
    carouselArea?: InjectableCss;
    viewport?: InjectableCss;
    track?: InjectableCss;
    slide?: InjectableCss;
    link?: LinkProps;
    imageContainer?: InjectableCss;
    name?: TypographyProps;
}

export const popularCategoriesCarouselStyles: PopularCategoriesCarouselStyles = {
    wrapper: {
        css: css`
            width: 100%;
            padding: 95px 0 24px 0;
        `,
    },
    heading: {
        css: css`
            font-size: 22px;
            font-weight: 600;
            text-align: center;
            margin-bottom: 40px;
        `,
    },
    carouselArea: {
        css: css`
            position: relative;
        `,
    },
    viewport: {
        css: css`
            overflow: hidden;
            padding: 6px 0;
        `,
    },
    track: {
        css: css`
            display: flex;
        `,
    },
    slide: {
        // slides per view: xs 2, sm 3, md 4, lg 6, xl 6
        css: css`
            flex: 0 0 auto;
            min-width: 0;
            box-sizing: border-box;
            padding: 0 15px;
            display: flex;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 33.3333%;
                    `,
                    css`
                        width: 25%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                ])}
        `,
    },
    link: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 12px 8px;
            border: 1px solid transparent;
            border-radius: 8px;
            color: ${getColor("text.main")};
            text-decoration: none;
            transition: 0.2s all ease;
            border: 1px solid ${getColor("common.border")};
            width: 100%;
            box-sizing: border-box;
                @media (max-width: 575px) {
                max-width: 260px;
            }
            &:hover {
                border-bottom-color: ${getColor("primary")};
                border-bottom-width: 2px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            }
            &:focus{
                outline: none;
            }
            ${TypographyStyle} {
                color: ${getColor("text.main")};
            }
        `,
    },
    imageContainer: {
        css: css`
            width: 100%;
            height: 130px;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                max-width: 100%;
                max-height: 130px;
                width: auto;
                height: auto;
                object-fit: contain;
            }
        `,
    },
    name: {
        css: css`
            margin-top: 12px;
            font-size: 14px;
            font-weight: 700;
            line-height: 1.3;
            text-align: center;
            display: block;
        `,
    },
};

const styles = popularCategoriesCarouselStyles;

const ArrowButton = styled.button<{ direction: "prev" | "next" }>`
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    ${({ direction }) => (direction === "prev" ? "left: -12px;" : "right: -12px;")}
    z-index: 1;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${getColor("secondary")};
    border-radius: 50%;
    background: ${getColor("primary.contrast")};
    cursor: pointer;
    &:disabled {
        visibility: hidden;
    }
    @media (max-width: 575px) {
        width: 32px;
        height: 32px;
        ${({ direction }) => (direction === "prev" ? "left: 30px;" : "right: 30px;")}
    }
`;

const Chevron: FC<{ direction: "prev" | "next" }> = ({ direction }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d={direction === "prev" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
    </svg>
);

const CategoryLink: FC<{ link: LinkFieldValue; openInNewWindow?: boolean; children?: React.ReactNode }> = props => {
    const url = useGetLink(props.link);
    return (
        <Link href={url?.url} {...styles.link} target={props.openInNewWindow ? "_blank" : "_self"}>
            {props.children}
        </Link>
    );
};

const CategoryCard: FC<{ item: CategoryModel }> = ({ item }) => (
    <CategoryLink link={item.fields.destination} openInNewWindow={item.fields.openInNewWindow}>
        <StyledWrapper {...styles.imageContainer}>
            {item.fields.image && <img src={item.fields.image} alt={item.fields.name} />}
        </StyledWrapper>
        <Typography as="span" {...styles.name}>
            {item.fields.name}
        </Typography>
    </CategoryLink>
);

const PopularCategoriesCarousel = ({ fields }: OwnProps) => {
    const items = fields.categories || [];

    const [emblaRef, embla] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps" });
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const updateScrollState = useCallback(() => {
        if (!embla) {
            return;
        }
        setCanScrollPrev(embla.canScrollPrev());
        setCanScrollNext(embla.canScrollNext());
    }, [embla]);

    const isPaused = useRef(false);
    useEffect(() => {
        if (!embla || !fields.autoScroll) {
            return;
        }
        if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            return;
        }
        const timer = window.setInterval(() => {
            if (isPaused.current || document.hidden) {
                return;
            }
            if (embla.canScrollNext()) {
                embla.scrollNext();
            } else {
                embla.scrollTo(0);
            }
        }, AUTO_SCROLL_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [embla, fields.autoScroll]);

    useEffect(() => {
        if (!embla) {
            return;
        }
        updateScrollState();
        embla.on("select", updateScrollState);
        embla.on("reInit", updateScrollState);
        return () => {
            embla.off("select", updateScrollState);
            embla.off("reInit", updateScrollState);
        };
    }, [embla, updateScrollState]);

    // re-measure when the editor adds/removes categories
    useEffect(() => {
        embla?.reInit();
    }, [embla, items.length]);

    if (items.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="popularCategoriesCarousel">
            {fields.heading && (
                <Typography variant="h2" {...styles.heading}>
                    {fields.heading}
                </Typography>
            )}
            <StyledWrapper {...styles.carouselArea}
                onMouseEnter={() => (isPaused.current = true)}
                onMouseLeave={() => (isPaused.current = false)}
                onFocus={() => (isPaused.current = true)}
                onBlur={() => (isPaused.current = false)}
            >
                <ArrowButton
                    type="button"
                    direction="prev"
                    aria-label="Previous"
                    disabled={!canScrollPrev}
                    onClick={() => embla?.scrollPrev()}
                >
                    <Chevron direction="prev" />
                </ArrowButton>
                <ArrowButton
                    type="button"
                    direction="next"
                    aria-label="Next"
                    disabled={!canScrollNext}
                    onClick={() => embla?.scrollNext()}
                >
                    <Chevron direction="next" />
                </ArrowButton>
                <StyledWrapper {...styles.viewport} ref={emblaRef}>
                    <StyledWrapper {...styles.track}>
                        {items.map((item, index) => (
                            <StyledWrapper {...styles.slide} key={`${item.fields.name}-${index}`}>
                                <CategoryCard item={item} />
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const PopularCategoriesCarouselModule: WidgetModule = {
    component: PopularCategoriesCarousel,
    definition: {
        displayName: "Home Page Popular Categories Carousel",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Shop from Popular Categories",
            },
            {
                name: fields.autoScroll,
                displayName: "Auto scroll",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.categories,
                displayName: "Categories",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Category Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "name",
                        displayName: "Category Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        displayName: "Link Url",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        displayName: "Open in new window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default PopularCategoriesCarouselModule;